import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Wrench, Upload, Download, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const EXPORTS = [["clients", "Clients"], ["vehicles", "Véhicules"], ["services", "Prestations"], ["appointments", "RDV"], ["quotes", "Devis"], ["invoices", "Factures"], ["payments", "Paiements"], ["products", "Stock"], ["suppliers", "Fournisseurs"], ["vehicle_protections", "Protections"]];
const AUTOS = [["inactive_3m", "Clients inactifs 3 mois"], ["inactive_6m", "Clients inactifs 6 mois"], ["inactive_12m", "Clients inactifs 12 mois"], ["protection_expiring", "Protection bientôt expirée"]];
const MAP_FIELDS = ["first_name", "last_name", "company", "phone", "email", "address", "zip", "city", "plate", "make", "model"];

export default function Outils() {
  const [settings, setSettings] = useState(null);
  const [csv, setCsv] = useState("");
  const [mapping, setMapping] = useState({});
  const [cols, setCols] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const text = await f.text(); setCsv(text);
    try { const { data } = await api.post("/import/clients", { csv_text: text, mapping: {}, commit: false }); setCols(data.columns || []); setPreview(data); const auto = {}; (data.columns || []).forEach((c) => { const l = c.toLowerCase(); if (l.includes("nom") && !l.includes("pré")) auto[c] = "last_name"; else if (l.includes("pré")) auto[c] = "first_name"; else if (l.includes("mail")) auto[c] = "email"; else if (l.includes("tel") || l.includes("phone")) auto[c] = "phone"; else if (l.includes("immat") || l.includes("plaq")) auto[c] = "plate"; else if (l.includes("marque")) auto[c] = "make"; else if (l.includes("mod")) auto[c] = "model"; }); setMapping(auto); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doImport = async (commit) => {
    setBusy(true);
    try { const { data } = await api.post("/import/clients", { csv_text: csv, mapping, commit }); setPreview(data); if (commit) toast.success(`${data.imported_clients} clients, ${data.imported_vehicles} véhicules importés · ${data.duplicates} doublons ignorés · ${data.errors} erreurs`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const doExport = async (entity) => {
    try { const r = await api.get(`/export/${entity}`, { responseType: "blob" }); const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `${entity}.csv`; a.click(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch }; setSettings(next);
    try { await api.put("/settings", patch); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleAuto = (k, v) => saveSettings({ automations: { ...(settings.automations || {}), [k]: v } });

  return (
    <div className="space-y-6">
      <PageHeader title="Outils & données" subtitle="Import, export, automatisations et paramètres de gestion" icon={Wrench} />

      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="import-card">
        <h3 className="font-heading text-base font-semibold text-slate-900 mb-1 flex items-center gap-2"><Upload className="w-4 h-4 text-emerald-600" /> Import clients / véhicules (CSV)</h3>
        <p className="text-xs text-slate-500 mb-3">Importez depuis un autre logiciel. Les doublons (e-mail, téléphone, immatriculation) sont détectés et jamais écrasés.</p>
        <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="import-file" className="text-sm" />
        {cols.length > 0 && (
          <div className="mt-3 space-y-2">
            <div className="text-xs font-medium text-slate-600">Correspondance des colonnes</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {cols.map((c) => (
                <div key={c} className="text-xs"><div className="text-slate-500 truncate mb-0.5">{c}</div>
                  <select value={mapping[c] || ""} onChange={(e) => setMapping({ ...mapping, [c]: e.target.value })} className="w-full h-8 rounded border border-slate-200 px-1" data-testid={`map-${c}`}>
                    <option value="">— ignorer —</option>{MAP_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              ))}
            </div>
            {preview && <div className="text-xs text-slate-600 mt-2" data-testid="import-preview-summary">{preview.total_rows} ligne(s) · {preview.duplicates} doublon(s) détecté(s){preview.committed ? ` · ${preview.imported_clients} clients + ${preview.imported_vehicles} véhicules importés` : ""}</div>}
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={() => doImport(false)} disabled={busy} data-testid="btn-import-preview" className="border-slate-200">Aperçu</Button>
              <Button size="sm" onClick={() => doImport(true)} disabled={busy} data-testid="btn-import-commit" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Importer</Button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="export-card">
        <h3 className="font-heading text-base font-semibold text-slate-900 mb-3 flex items-center gap-2"><Download className="w-4 h-4 text-emerald-600" /> Export des données (CSV)</h3>
        <div className="flex flex-wrap gap-2">
          {EXPORTS.map(([v, l]) => <Button key={v} size="sm" variant="outline" onClick={() => doExport(v)} data-testid={`btn-export-${v}`} className="border-slate-200">{l}</Button>)}
        </div>
      </div>

      {settings && (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="automations-card">
            <h3 className="font-heading text-base font-semibold text-slate-900 mb-1">Relances automatiques</h3>
            <p className="text-xs text-slate-500 mb-3">Activez uniquement ce que vous souhaitez. Rien n'est envoyé sans activation explicite. Anti-doublon intégré.</p>
            <div className="space-y-2">
              {AUTOS.map(([k, l]) => (
                <div key={k} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <span className="text-sm text-slate-700">{l}</span>
                  <Switch checked={!!(settings.automations || {})[k]} onCheckedChange={(v) => toggleAuto(k, v)} data-testid={`auto-${k}`} />
                </div>
              ))}
              <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <span className="text-sm text-slate-700">Alerte facture impayée après (jours)</span>
                <Input type="number" value={(settings.automations || {}).invoice_unpaid_days || ""} onChange={(e) => toggleAuto("invoice_unpaid_days", Number(e.target.value) || 0)} data-testid="auto-unpaid-days" className="w-24 h-8 bg-white border-slate-200" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-sm" data-testid="labor-card">
            <h3 className="font-heading text-base font-semibold text-slate-900 mb-1">Coût de main-d'œuvre</h3>
            <p className="text-xs text-slate-500 mb-3">Utilisé pour la rentabilité (0 = sans impact).</p>
            <Label className="text-xs text-slate-500">Coût horaire (€)</Label>
            <div className="flex gap-2 mt-1">
              <Input type="number" value={settings.labor_hourly_cost ?? 0} onChange={(e) => setSettings({ ...settings, labor_hourly_cost: Number(e.target.value) })} data-testid="labor-hourly-cost" className="bg-slate-50 border-slate-200" />
              <Button size="sm" onClick={() => saveSettings({ labor_hourly_cost: Number(settings.labor_hourly_cost) || 0 })} data-testid="btn-save-labor" className="bg-emerald-500 hover:bg-emerald-600 text-black"><Save className="w-4 h-4" /></Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
